import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const setAuthToken = (token) => {
  if (token) {
    const authorization = `Bearer ${token}`;
    api.defaults.headers.common.Authorization = authorization;
    axios.defaults.headers.common.Authorization = authorization;
    return;
  }
  delete api.defaults.headers.common.Authorization;
  delete axios.defaults.headers.common.Authorization;
};

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || '';
    const isAuthBootstrapRequest = [
      '/auth/login', '/auth/register', '/auth/me', '/auth/logout',
    ].some((path) => requestUrl.includes(path));
    if (error.response?.status === 401 && !isAuthBootstrapRequest) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
  changePassword: (current_password, new_password) =>
    api.post('/auth/change-password', { current_password, new_password }),
  logout: () => api.post('/auth/logout'),
};

// Users API
export const usersAPI = {
  list: () => api.get('/users'),
  directory: () => api.get('/users/directory'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  listPending: () => api.get('/users/pending'),
  approve: (id) => api.post(`/users/${id}/approve?action=approve`),
  reject: (id, reason) => api.post(`/users/${id}/approve?action=reject&reason=${encodeURIComponent(reason)}`),
  resetPassword: (id, new_password) => api.post(`/users/${id}/reset-password`, { new_password }),
};

// Customers API
export const customersAPI = {
  list: (params) => api.get('/customers', { params }),
  get: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  getHistory: (id) => api.get(`/customers/${id}/history`),
  duplicates: () => api.get('/customers/duplicates'),
  merge: (data) => api.post('/customers/merge', data),
  similar: (params) => api.get('/customers/similar', { params }),
};

// 360° History API
export const historyAPI = {
  comparCostings: (baselineId, compareId) => api.get(`/costings/${baselineId}/compare/${compareId}`),
};

// Wire Costing API (per-kg, simpler than fastener costings)
export const wireCostingsAPI = {
  preview: (data) => api.post('/wire-costings/preview', data),
  create: (data) => api.post('/wire-costings', data),
  update: (id, data) => api.put(`/wire-costings/${id}`, data),
};

// Wire Quotations API
export const wireQuotationsAPI = {
  list: (params) => api.get('/wire-quotations', { params }),
  create: (data) => api.post('/wire-quotations', data),
  get: (id) => api.get(`/wire-quotations/${id}`),
  addItem: (id, item) => api.post(`/wire-quotations/${id}/items`, item),
  updateItem: (id, itemId, item) => api.put(`/wire-quotations/${id}/items/${itemId}`, item),
  deleteItem: (id, itemId) => api.delete(`/wire-quotations/${id}/items/${itemId}`),
};

// Machines API
export const machinesAPI = {
  list: (params) => api.get('/machines', { params }),
  get: (id) => api.get(`/machines/${id}`),
  create: (data) => api.post('/machines', data),
  update: (id, data) => api.put(`/machines/${id}`, data),
  delete: (id) => api.delete(`/machines/${id}`),
  getNextAvailableDate: (id) => api.get(`/machines/${id}/next-available-date`),
};

// Product Types API
export const productTypesAPI = {
  list: () => api.get('/product-types'),
  create: (data) => api.post('/product-types', data),
  update: (id, data) => api.put(`/product-types/${id}`, data),
  delete: (id) => api.delete(`/product-types/${id}`),
};

// Standards API
export const standardsAPI = {
  list: () => api.get('/standards'),
  create: (data) => api.post('/standards', data),
  update: (id, data) => api.put(`/standards/${id}`, data),
  delete: (id) => api.delete(`/standards/${id}`),
};

// Parts Master API
export const partsAPI = {
  list: (params) => api.get('/parts', { params }),
  get: (id) => api.get(`/parts/${id}`),
  create: (data) => api.post('/parts', data),
  update: (id, data) => api.put(`/parts/${id}`, data),
  delete: (id) => api.delete(`/parts/${id}`),
  duplicate: (id) => api.post(`/parts/${id}/duplicate`),
  search: (query) => api.get('/parts', { params: { search: query, limit: 20 } }),
  importExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/parts/import-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Inquiries API
export const inquiriesAPI = {
  list: (params) => api.get('/inquiries', { params }),
  get: (id) => api.get(`/inquiries/${id}`),
  create: (data) => api.post('/inquiries', data),
  update: (id, data) => api.put(`/inquiries/${id}`, data),
  updateStatus: (id, data) => api.put(`/inquiries/${id}/status`, data),
  addItems: (id, items) => api.post(`/inquiries/${id}/items`, items),
  deleteItem: (id, itemId) => api.delete(`/inquiries/${id}/items/${itemId}`),
  bulkDeleteItems: (id, itemIds) => api.post(`/inquiries/${id}/items/bulk-delete`, { item_ids: itemIds }),
  uploadDocument: (id, file, category = 'customer_inquiry', inquiryProductId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    if (inquiryProductId) formData.append('inquiry_product_id', inquiryProductId);
    return api.post(`/inquiries/${id}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadDocument: (id, documentId, asPng = false) => api.get(`/inquiries/${id}/documents/${documentId}/download${asPng ? '?as_png=true' : ''}`, { responseType: 'blob' }),
  deleteDocument: (id, documentId) => api.delete(`/inquiries/${id}/documents/${documentId}`),
  extractDocumentItems: (id, documentId) => api.post(`/inquiries/${id}/documents/${documentId}/extract-items`),
  extractDocumentStatus: (id, documentId) => api.get(`/inquiries/${id}/documents/${documentId}/extract-status`),
  previewDocument: (id, documentId) => api.get(`/inquiries/${id}/documents/${documentId}/preview`),
  updateDocument: (id, documentId, data) => api.patch(`/inquiries/${id}/documents/${documentId}`, data),
  saveFeasibility: (id, data) => api.put(`/inquiries/${id}/feasibility`, data),
  updateItem: (id, itemId, data) => api.patch(`/inquiries/${id}/items/${itemId}`, data),
  createItemPart: (id, itemId, data) => api.post(`/inquiries/${id}/items/${itemId}/create-part`, data || {}),
  partSuggestions: (id, itemId) => api.get(`/inquiries/${id}/items/${itemId}/part-suggestions`),
  approveItemCosting: (id, itemId, costingId) =>
    api.post(`/inquiries/${id}/items/${itemId}/approve-costing`, { costing_id: costingId }),
  approveSuggestedCostings: (id, itemIds) =>
    api.post(`/inquiries/${id}/approve-costings`, { item_ids: itemIds || [] }),
  linkableQuotations: (id) => api.get(`/inquiries/${id}/linkable-quotations`),
  linkQuotation: (id, quotationIds) =>
    api.post(`/inquiries/${id}/link-quotation`, { quotation_ids: quotationIds }),
  unlinkQuotation: (id, quotationId) =>
    api.post(`/inquiries/${id}/unlink-quotation`, { quotation_id: quotationId }),
  addNote: (id, note) => api.post(`/inquiries/${id}/activities`, { note }),
  createQuotation: (id, data) => api.post(`/inquiries/${id}/quotations`, data),
  createVendorRfq: (id, data) => api.post(`/inquiries/${id}/vendor-rfqs`, data),
  vendorComparison: (id) => api.get(`/inquiries/${id}/vendor-comparison`),
  cycleTime: (id) => api.get(`/inquiries/${id}/cycle-time`),
  addTimeLog: (id, data) => api.post(`/inquiries/${id}/time-logs`, data),
  deleteTimeLog: (id, logId) => api.delete(`/inquiries/${id}/time-logs/${logId}`),
};

export const vendorsAPI = {
  list: (params) => api.get('/vendors', { params }),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
};

export const vendorRfqsAPI = {
  get: (id) => api.get(`/vendor-rfqs/${id}`),
  addResponse: (id, data) => api.post(`/vendor-rfqs/${id}/responses`, data),
  downloadExcel: (id) => api.get(`/vendor-rfqs/${id}/excel`, { responseType: 'blob' }),
  emailContext: (id) => api.get(`/vendor-rfqs/${id}/email-context`),
  sendEmail: (id, formData) => api.post(`/vendor-rfqs/${id}/send-email`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadQuote: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/vendor-rfqs/${id}/quote-uploads`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getQuoteImport: (importId) => api.get(`/vendor-quote-imports/${importId}`),
  confirmQuoteImport: (importId, data) => api.post(`/vendor-quote-imports/${importId}/confirm`, data),
};

// Process Flows API
export const processFlowsAPI = {
  list: (params) => api.get('/process-flows', { params }),
  get: (id) => api.get(`/process-flows/${id}`),
  create: (data) => api.post('/process-flows', data),
  update: (id, data) => api.put(`/process-flows/${id}`, data),
  delete: (id) => api.delete(`/process-flows/${id}`),
  duplicate: (id, targetPartId = null) => api.post(`/process-flows/${id}/duplicate`, null, {
    params: targetPartId ? { target_part_id: targetPartId } : {}
  }),
  downloadPdf: (id) => api.get(`/process-flows/${id}/pdf`, { responseType: 'blob' }),
};

// Costings API
export const costingsAPI = {
  list: (params) => api.get('/costings', { params }),
  get: (id) => api.get(`/costings/${id}`),
  create: (data) => api.post('/costings', data),
  createKit: (data) => api.post('/costings/kit', data),
  approve: (id, data) => api.post(`/costings/${id}/approve`, data),
  reject: (id, data) => api.post(`/costings/${id}/reject`, data),
  lock: (id) => api.post(`/costings/${id}/lock`),
  clone: (id) => api.post(`/costings/${id}/clone`),
  delete: (id) => api.delete(`/costings/${id}`),
  downloadPdf: (id) => api.get(`/costings/${id}/pdf`, { responseType: 'blob' }),
  getByPart: (partId) => api.get('/costings', { params: { part_id: partId, is_active: true } }),
  search: (query) => api.get('/costings', { params: { search: query, is_active: true, limit: 50 } }),
};

export const costingImportsAPI = {
  upload: (inquiryId, inquiryProductId, file, mode = 'reference') => {
    const formData = new FormData();
    if (inquiryProductId) formData.append('inquiry_product_id', inquiryProductId);
    formData.append('mode', mode);
    formData.append('file', file);
    return api.post(`/inquiries/${inquiryId}/costing-imports`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  get: (id) => api.get(`/costing-imports/${id}`),
  extract: (id) => api.post(`/costing-imports/${id}/extract`),
  update: (id, data) => api.put(`/costing-imports/${id}`, data),
  confirm: (id) => api.post(`/costing-imports/${id}/confirm`),
  updateRow: (id, rowId, data) => api.put(`/costing-imports/${id}/rows/${rowId}`, data),
  discardRow: (id, rowId) => api.delete(`/costing-imports/${id}/rows/${rowId}`),
  approveRow: (id, rowId) => api.post(`/costing-imports/${id}/rows/${rowId}/approve`),
  approveAll: (id) => api.post(`/costing-imports/${id}/approve-all`),
  createQuotation: (id, data) => api.post(`/costing-imports/${id}/create-quotation`, data),
};

export const customerPosAPI = {
  list: (params) => api.get('/customer-pos', { params }),
  get: (id) => api.get(`/customer-pos/${id}`),
  create: (data) => {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') formData.append(key, value);
    });
    return api.post('/customer-pos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  update: (id, data) => api.put(`/customer-pos/${id}`, data),
  download: (id) => api.get(`/customer-pos/${id}/download`, { responseType: 'blob' }),
  extract: (id) => api.post(`/customer-pos/${id}/extract`),
  createSalesOrder: (id, payload = {}) => api.post(`/customer-pos/${id}/create-sales-order`, {
    selected_item_ids: payload.selected_item_ids || [],
    lines: payload.lines || [],
    ship_to_address: payload.ship_to_address ?? null,
    close_inquiry: payload.close_inquiry !== false,
  }),
};

// BOM (Bill of Materials) API - NEW
export const bomAPI = {
  list: (params) => api.get('/bom', { params }),
  get: (id) => api.get(`/bom/${id}`),
  create: (data) => api.post('/bom', data),
  update: (id, data) => api.put(`/bom/${id}`, data),
  acceptPrice: (id) => api.post(`/bom/${id}/accept-price`),
  getByPart: (partId) => api.get('/bom', { params: { part_id: partId, is_active: true } }),
};

// Quotations API
export const quotationsAPI = {
  list: (params) => api.get('/quotations', { params }),
  get: (id) => api.get(`/quotations/${id}`),
  create: (data) => api.post('/quotations', data),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  delete: (id) => api.delete(`/quotations/${id}`),
  addItems: (id, data) => api.post(`/quotations/${id}/add-items`, data),
  refreshKitPrices: (id) => api.post(`/quotations/${id}/refresh-kit-prices`),
  removeItem: (quotationId, itemId) => api.delete(`/quotations/${quotationId}/items/${itemId}`),
  updateItem: (quotationId, itemId, data) => api.put(`/quotations/${quotationId}/items/${itemId}`, data),
  updateStatus: (id, status, lostReason, lostRemarks) => 
    api.put(`/quotations/${id}/status?status=${status}${lostReason ? `&lost_reason=${lostReason}` : ''}${lostRemarks ? `&lost_remarks=${encodeURIComponent(lostRemarks)}` : ''}`),
  convertToOrder: (id, selectedItemIds) => api.post(`/quotations/${id}/convert-to-order`, selectedItemIds),
  approve: (id) => api.post(`/quotations/${id}/approve`),
  release: (id) => api.post(`/quotations/${id}/release`),
  revise: (id) => api.post(`/quotations/${id}/revise`),
  downloadPdf: (id) => api.get(`/quotations/${id}/pdf`, { responseType: 'blob' }),
  // Email functionality
  getEmailContext: (id) => api.get(`/quotations/${id}/email-context`),
  // New: Send email with frontend-generated PDF blob
  sendEmailWithPdf: (id, formData) => api.post(`/quotations/${id}/send-email-with-pdf`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Deprecated: Old endpoint (uses backend-generated PDF)
  sendEmail: (id, data) => api.post(`/quotations/${id}/send-email`, data),
  syncParts: (id, dryRun = false) => api.post(`/quotations/${id}/sync-parts${dryRun ? '?dry_run=true' : ''}`),
  lastQuotes: (partIds, excludeQuotationId) => api.get('/last-quotes', {
    params: {
      part_ids: (partIds || []).join(','),
      ...(excludeQuotationId ? { exclude_quotation_id: excludeQuotationId } : {}),
    },
  }),
};

// Sales Orders API
export const ordersAPI = {
  list: (params) => api.get('/orders', { params }),
  get: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  createDirect: (data) => api.post('/orders/direct', data),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  syncParts: (id, dryRun = false) => api.post(`/orders/${id}/sync-parts${dryRun ? '?dry_run=true' : ''}`),
};

// Work Orders API (Production Planning)
export const workOrdersAPI = {
  list: (params) => api.get('/work-orders', { params }),
  get: (id) => api.get(`/work-orders/${id}`),
  create: (data) => api.post('/work-orders', data),
  update: (id, data) => api.put(`/work-orders/${id}`, data),
  delete: (id) => api.delete(`/work-orders/${id}`),
  release: (id) => api.post(`/work-orders/${id}/release`),
  start: (id) => api.post(`/work-orders/${id}/start`),
  complete: (id, params) => api.post(`/work-orders/${id}/complete`, null, { params }),
  getBacklog: () => api.get('/work-orders/backlog'),
  getPlanning: (params) => api.get('/work-orders/planning', { params }),
  getByOrder: (orderId) => api.get(`/work-orders/by-order/${orderId}`),
};

// Reports API
export const reportsAPI = {
  rmRequirements: (params) => api.get('/reports/rm-requirements', { params }),
};

// Dashboard API
export const dashboardAPI = {
  user: () => api.get('/dashboard/user'),
  manager: () => api.get('/dashboard/manager'),
  admin: (params) => api.get('/dashboard/admin', { params }),
  cycleTime: (params) => api.get('/dashboard/cycle-time', { params }),
  factoryReset: () => api.delete('/admin/reset-database'),
};

export const orderReviewsAPI = {
  getForOrder: (orderId) => api.get(`/orders/${orderId}/review-sheet`),
  create: (orderId) => api.post(`/orders/${orderId}/review-sheet`),
  get: (id) => api.get(`/order-reviews/${id}`),
  save: (id, data) => api.put(`/order-reviews/${id}`, data),
  statuses: () => api.get('/order-reviews/statuses'),
  worklist: (params) => api.get('/order-reviews/worklist', { params }),
  list: (params) => api.get('/order-reviews', { params }),
  markDownloaded: (id) => api.post(`/order-reviews/${id}/mark-downloaded`),
  uploadSigned: (id, formData) => api.post(`/order-reviews/${id}/signed-files`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  signedFileUrl: (id, fileId) => `/order-reviews/${id}/signed-files/${fileId}`,
  downloadSigned: (id, fileId) => api.get(`/order-reviews/${id}/signed-files/${fileId}`, { responseType: 'blob' }),
  deleteSigned: (id, fileId) => api.delete(`/order-reviews/${id}/signed-files/${fileId}`),
};

export const followupsAPI = {
  people: (inquiryId) => api.get(`/inquiries/${inquiryId}/people`),
  setAssignment: (inquiryId, data) => api.put(`/inquiries/${inquiryId}/assignment`, data),
  list: (inquiryId) => api.get(`/inquiries/${inquiryId}/followups`),
  create: (inquiryId, data) => api.post(`/inquiries/${inquiryId}/followups`, data),
  update: (followupId, data) => api.patch(`/followups/${followupId}`, data),
  remove: (followupId) => api.delete(`/followups/${followupId}`),
  mine: (params) => api.get('/followups/my', { params }),
  bulkAssign: (data) => api.post('/inquiries/bulk-assign', data),
};

// Audit Logs API
export const auditLogsAPI = {
  list: (params) => api.get('/audit-logs', { params }),
};

// Bulk multi-select operations (soft delete / status change / restore)
export const bulkAPI = {
  previewDelete: (entity, ids) => api.post(`/bulk/${entity}/preview-delete`, { ids }),
  delete: (entity, ids, confirm) => api.post(`/bulk/${entity}/delete`, { ids, confirm }),
  status: (entity, ids, status, reason, notes) =>
    api.post(`/bulk/${entity}/status`, { ids, status, reason, notes }),
  recycleBin: (params) => api.get('/bulk/recycle-bin', { params }),
  purge: (entity, ids, confirm) => api.post(`/bulk/${entity}/purge`, { ids, confirm }),
  restore: (entity, ids) => api.post(`/bulk/${entity}/restore`, { ids }),
};

// Export API (Admin only)
export const exportAPI = {
  costings: (format = 'xlsx') => api.get(`/export/costings?format=${format}`, { responseType: 'blob' }),
  quotations: (format = 'xlsx') => api.get(`/export/quotations?format=${format}`, { responseType: 'blob' }),
  parts: (format = 'xlsx') => api.get(`/export/parts?format=${format}`, { responseType: 'blob' }),
};

// System Parameters API
export const systemParametersAPI = {
  list: () => api.get('/system-parameters'),
  update: (key, value) => api.put(`/system-parameters/${key}`, { value }),
};

// AI Document Processing API
export const aiDocumentsAPI = {
  // Upload document for processing
  upload: (file, documentType, customerId = null, inquiryId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    if (customerId) formData.append('customer_id', customerId);
    if (inquiryId) formData.append('inquiry_id', inquiryId);
    return api.post('/ai/upload-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Process uploaded document with AI
  process: (documentId) => api.post(`/ai/process-document/${documentId}`),
  // List all processed documents
  listDocuments: (params) => api.get('/ai/documents', { params }),
  // Draft parts endpoints
  listDraftParts: (params) => api.get('/ai/draft-parts', { params }),
  getDraftPart: (id) => api.get(`/ai/draft-parts/${id}`),
  updateDraftPart: (id, data) => api.put(`/ai/draft-parts/${id}`, data),
  approveDraftPart: (id, data) => api.post(`/ai/draft-parts/${id}/approve`, data || {}),
  rejectDraftPart: (id, data) => api.post(`/ai/draft-parts/${id}/reject`, data || {}),
  // Draft orders endpoints
  listDraftOrders: (params) => api.get('/ai/draft-orders', { params }),
  getDraftOrder: (id) => api.get(`/ai/draft-orders/${id}`),
  updateDraftOrder: (id, data) => api.put(`/ai/draft-orders/${id}`, data),
  approveDraftOrder: (id, data) => api.post(`/ai/draft-orders/${id}/approve`, data || {}),
  rejectDraftOrder: (id, data) => api.post(`/ai/draft-orders/${id}/reject`, data || {}),
  quickCreatePart: (draftId, itemId, data) => 
    api.post(`/ai/draft-orders/${draftId}/quick-create-part?item_id=${itemId}`, data),
  // Process flow suggestions
  suggestProcessFlow: (description) => api.get('/ai/suggest-process-flow', { params: { description } }),
};

// File Upload API
export const uploadAPI = {
  drawing: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/drawing', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Label Generator APIs
export const labelTemplatesAPI = {
  list: (params) => api.get('/label-templates', { params }),
  get: (id) => api.get(`/label-templates/${id}`),
  create: (data) => api.post('/label-templates', data),
  update: (id, data) => api.put(`/label-templates/${id}`, data),
  delete: (id) => api.delete(`/label-templates/${id}`),
  getByCustomer: (customerId) => api.get(`/label-templates/by-customer/${customerId}`),
  getForCustomer: (customerId, includeGeneric = true) => 
    api.get(`/label-templates/for-customer/${customerId}`, { params: { include_generic: includeGeneric } }),
  aiVisionDraft: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/label-templates/ai-vision-draft', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const labelHistoryAPI = {
  list: (params) => api.get('/label-history', { params }),
  get: (id) => api.get(`/label-history/${id}`),
  create: (data) => api.post('/label-history', data),
};

export const labelGeneratorAPI = {
  searchParts: (q, customerId) => api.get('/label-generator/parts-search', { params: { q, customer_id: customerId } }),
  getOrdersForPart: (partId) => api.get(`/label-generator/orders-for-part/${partId}`),
  getFieldData: (orderType, orderId, partId, itemId) => 
    api.get(`/label-generator/field-data/${orderType}/${orderId}`, { params: { part_id: partId, item_id: itemId } }),
};

// Email Accounts API (Admin only)
export const emailAccountsAPI = {
  list: () => api.get('/email-accounts'),
  create: (data) => api.post('/email-accounts', data),
  delete: (id) => api.delete(`/email-accounts/${id}`),
  toggle: (id) => api.put(`/email-accounts/${id}/toggle`),
  test: (id) => api.post(`/email-accounts/${id}/test`),
};

// Email Ingestion API
export const emailIngestionAPI = {
  sync: () => api.post('/email-ingestion/sync'),
  getSyncStatus: () => api.get('/email-ingestion/sync-status'),
  getDashboardStats: () => api.get('/email-ingestion/dashboard-stats'),
  getSyncInterval: () => api.get('/email-ingestion/sync-interval'),
  setSyncInterval: (interval_minutes) => api.put('/email-ingestion/sync-interval', { interval_minutes }),
  list: (params) => api.get('/ingested-emails', { params }),
  get: (id) => api.get(`/ingested-emails/${id}`),
  updateStatus: (id, status) => api.put(`/ingested-emails/${id}/status`, { status }),
  pushToParts: (id) => api.post(`/ingested-emails/${id}/push-to-parts`),
  downloadAttachment: (id) => `${API_URL}/email-attachments/${id}/download`,
};

// Admin Backup API
export const backupAPI = {
  getStatus: () => api.get('/admin/backup/status'),
  updateSettings: (data) => api.put('/admin/backup/settings', data),
  download: () => `${API_URL}/admin/backup/download`,
  downloadWithAuth: () => api.get('/admin/backup/download', { responseType: 'blob' }),
};

// Material Master API
export const materialMasterAPI = {
  list: () => api.get('/raw-materials'),
  get: (id) => api.get(`/raw-materials/${id}`),
  create: (data) => api.post('/raw-materials', data),
  update: (id, data) => api.put(`/raw-materials/${id}`, data),
  delete: (id) => api.delete(`/raw-materials/${id}`),
};

// PDF Generator API
export const pdfAPI = {
  generate: (html, orientation, filename, options = {}) =>
    api.post('/pdf/generate', { html, orientation, filename, page_numbers: Boolean(options.pageNumbers) }, { responseType: 'blob' }),
};

export default api;
